/**
 * ReIndexingVouchersScreen — Resequence voucher numbers for a selected voucher type.
 * Finance Masters → Reindex Vouchers
 */
import React, { useState } from 'react';
import { RefreshCw, AlertTriangle, CheckCircle } from 'lucide-react';
import { erpToast } from '../../components/erp/ERPToast';
import { API_BASE as API, getToken } from '../../lib/api';
import '../../styles/erp-theme.css';

const hdrs = () => ({ Authorization: `Bearer ${getToken() || ''}`, 'Content-Type': 'application/json' });

const VOUCHER_TYPES = [
  { key: 'cash-receipts',       label: 'Cash Receipts',       transType: 'CR' },
  { key: 'bank-receipts',       label: 'Bank Receipts',       transType: 'BR' },
  { key: 'pdc-receipts',        label: 'PDC Receipts',        transType: 'PR' },
  { key: 'cash-payments',       label: 'Cash Payments',       transType: 'CP' },
  { key: 'bank-payments',       label: 'Bank Payments',       transType: 'BP' },
  { key: 'pdc-payments',        label: 'PDC Payments',        transType: 'PP' },
  { key: 'petty-cash-receipts', label: 'Petty Cash Receipts', transType: 'PCR' },
  { key: 'petty-cash-payments', label: 'Petty Cash Payments', transType: 'PCP' },
  { key: 'journal-entries',     label: 'Journal Entries',     transType: 'JV' },
  { key: 'contra-entries',      label: 'Contra Entries',      transType: 'CE' },
  { key: 'debit-notes',         label: 'Debit Notes',         transType: 'DN' },
  { key: 'credit-notes',        label: 'Credit Notes',        transType: 'CN' },
  { key: 'purchase-vouchers',   label: 'Purchase Vouchers',   transType: 'PV' },
  { key: 'sales-vouchers',      label: 'Sales Vouchers',      transType: 'SV' },
];

const ReIndexingVouchersScreen = ({ theme = 'light' }) => {
  const [voucherType, setVoucherType] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [confirmed, setConfirmed] = useState(false);

  const handleReindex = async () => {
    if (!voucherType) { erpToast.error('Please select a voucher type'); return; }
    if (!confirmed) { erpToast.warning('Please confirm before proceeding'); return; }
    setLoading(true);
    setResult(null);
    try {
      const r = await fetch(`${API}/api/finance/reindex-vouchers`, {
        method: 'POST',
        headers: hdrs(),
        body: JSON.stringify({ voucherType, fromDate: fromDate || undefined, toDate: toDate || undefined }),
      });
      const j = await r.json();
      if (j.success) {
        setResult({ ok: true, message: j.message || 'Reindex complete', data: j.data });
        erpToast.success(j.message || 'Reindex complete');
      } else {
        setResult({ ok: false, message: j.error || 'Reindex failed' });
        erpToast.error(j.error || 'Reindex failed');
      }
    } catch (e) {
      setResult({ ok: false, message: e.message });
      erpToast.error('Reindex request failed');
    }
    setLoading(false);
    setConfirmed(false);
  };

  return (
    <div className={`erp-screen${theme === 'dark' ? ' dark' : ''}`} style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div className="erp-screen-header" style={{ padding: '12px 20px', borderBottom: '1px solid var(--erp-border)', flexShrink: 0 }}>
        <h2 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: 'var(--erp-text)' }}>Reindex Vouchers</h2>
        <p style={{ margin: '2px 0 0', fontSize: 11, color: 'var(--erp-text-muted)' }}>Resequence voucher document numbers for the selected transaction type</p>
      </div>

      {/* Warning */}
      <div style={{ background: '#fff8e1', border: '1px solid #ffc107', borderRadius: 6, margin: 20, padding: '10px 14px', display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 11 }}>
        <AlertTriangle size={14} color="#f59e0b" style={{ flexShrink: 0, marginTop: 1 }} />
        <div>
          <strong>Caution:</strong> Reindexing will renumber all vouchers of the selected type in chronological order.
          This operation is irreversible. Ensure no other users are working on these vouchers before proceeding.
        </div>
      </div>

      {/* Form */}
      <div style={{ padding: '0 20px', display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 500 }}>
        <div className="erp-form-row">
          <label className="erp-form-label required" style={{ minWidth: 130 }}>Voucher Type</label>
          <div className="erp-form-control">
            <select
              value={voucherType}
              onChange={e => { setVoucherType(e.target.value); setResult(null); setConfirmed(false); }}
              className="erp-input erp-select"
              data-testid="reindex-voucher-type"
            >
              <option value="">-- Select Voucher Type --</option>
              {VOUCHER_TYPES.map(v => (
                <option key={v.key} value={v.key}>{v.label} ({v.transType})</option>
              ))}
            </select>
          </div>
        </div>

        <div className="erp-form-row">
          <label className="erp-form-label" style={{ minWidth: 130 }}>From Date <span style={{ color: 'var(--erp-text-muted)' }}>(optional)</span></label>
          <div className="erp-form-control">
            <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} className="erp-input" data-testid="reindex-from-date" />
          </div>
        </div>

        <div className="erp-form-row">
          <label className="erp-form-label" style={{ minWidth: 130 }}>To Date <span style={{ color: 'var(--erp-text-muted)' }}>(optional)</span></label>
          <div className="erp-form-control">
            <input type="date" value={toDate} onChange={e => setToDate(e.target.value)} className="erp-input" data-testid="reindex-to-date" />
          </div>
        </div>

        {voucherType && (
          <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12, cursor: 'pointer', color: 'var(--erp-danger)' }}>
            <input type="checkbox" checked={confirmed} onChange={e => setConfirmed(e.target.checked)} data-testid="reindex-confirm-check" />
            I understand this will renumber all <strong>{VOUCHER_TYPES.find(v => v.key === voucherType)?.label}</strong> vouchers and this cannot be undone.
          </label>
        )}

        <div>
          <button
            className="erp-btn erp-btn-primary"
            onClick={handleReindex}
            disabled={loading || !voucherType || !confirmed}
            style={{ height: 34, fontSize: 12, display: 'flex', alignItems: 'center', gap: 6 }}
            data-testid="reindex-submit-btn"
          >
            <RefreshCw size={13} className={loading ? 'spin' : ''} />
            {loading ? 'Reindexing...' : 'Start Reindex'}
          </button>
        </div>

        {result && (
          <div style={{ display: 'flex', gap: 10, padding: '10px 14px', borderRadius: 6, background: result.ok ? '#e8f5e9' : '#fce4ec', border: `1px solid ${result.ok ? '#a5d6a7' : '#ef9a9a'}`, fontSize: 12 }}>
            {result.ok ? <CheckCircle size={14} color="#4caf50" /> : <AlertTriangle size={14} color="#e53935" />}
            <div>
              <strong>{result.ok ? 'Success' : 'Error'}:</strong> {result.message}
              {result.data && <div style={{ marginTop: 4, color: 'var(--erp-text-muted)' }}>Vouchers processed: {result.data.reindexed ?? 'N/A'}</div>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReIndexingVouchersScreen;
